import { useCallback, useEffect, useState } from "react";
import { Loader2, TrendingUp, Wallet, AlertTriangle, Users2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const euro = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const YEARS = [2026, 2027, 2025];

export const CommissionsOverview = () => {
  const [year, setYear] = useState(2026);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = useCallback(() => {
    setLoading(true);
    api.get("/phase3/admin/commissions-overview", { params: { year } })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Impossible de charger la vue d'ensemble"))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  if (loading || !data) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;

  const cards = [
    { label: "Commissions facturées", value: data.totals.facture, icon: TrendingUp, testid: "overview-facture" },
    { label: "Encaissé (part Centrale)", value: data.totals.part_centrale, icon: Wallet, testid: "overview-encaisse" },
    { label: "Impayé (part Centrale)", value: data.totals.impaye, icon: AlertTriangle, testid: "overview-impaye" },
    { label: "Reversé aux partenaires", value: data.totals.part_partenaires, icon: Users2, testid: "overview-part-partenaires" },
  ];

  return (
    <div data-testid="commissions-overview">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Réseau Commissions — Année civile</p>
        <select
          data-testid="overview-year-select"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-bold text-navy focus:border-cognac focus:outline-none"
        >
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="rounded-2xl border border-slate-200 bg-white p-5">
            <c.icon className="h-5 w-5 text-cognac" />
            <p className="mt-3 font-display text-2xl font-semibold text-navy">{euro(c.value)}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <p className="border-b border-slate-100 px-5 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Par partenaire</p>
          {data.by_partner.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-400">Aucune commission cette année.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <tbody>
                {data.by_partner.map((p) => (
                  <tr key={p.partner_company_id} data-testid={`overview-partner-row-${p.partner_company_id}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-3 font-bold text-navy">{p.partner_name}</td>
                    <td className="px-5 py-3 text-right text-slate-500">{p.count} marché(s)</td>
                    <td className="px-5 py-3 text-right font-semibold text-navy">{euro(p.encaisse)} encaissé</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <p className="border-b border-slate-100 px-5 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Par entreprise</p>
          {data.by_company.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-400">Aucune commission cette année.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <tbody>
                {data.by_company.map((c) => (
                  <tr key={c.company_id} data-testid={`overview-company-row-${c.company_id}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-3 font-bold text-navy">{c.company_name}</td>
                    <td className="px-5 py-3 text-right text-slate-500">{c.count} marché(s)</td>
                    <td className="px-5 py-3 text-right font-semibold text-navy">{euro(c.commission_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <p className="border-b border-slate-100 px-5 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Évolution mensuelle</p>
        {data.by_month.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-400">Aucune donnée.</p>
        ) : (
          <div className="space-y-2 p-5">
            {data.by_month.map((m) => {
              const max = Math.max(...data.by_month.map((x) => x.commission_total), 1);
              return (
                <div key={m.month} data-testid={`overview-month-${m.month}`} className="flex items-center gap-3 text-sm">
                  <span className="w-16 shrink-0 text-slate-400">{m.month}</span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-cream">
                    <div className="h-full rounded-full bg-cognac" style={{ width: `${(m.commission_total / max) * 100}%` }} />
                  </div>
                  <span className="w-24 shrink-0 text-right font-semibold text-navy">{euro(m.commission_total)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
